import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Recognises a failed dynamic import. Wording differs per engine, so this matches on
 * the shared shape rather than one browser's exact sentence:
 *   Chrome/Edge  "Failed to fetch dynamically imported module: <url>"
 *   Firefox      "error loading dynamically imported module"
 *   Safari       "Importing a module script failed."
 *   Webpack-era  ChunkLoadError
 *
 * Exported because callers that `await import()` a heavy library on demand must catch
 * their own rejection — an *unhandled* one reaches the listener below and reloads the
 * whole app, throwing away whatever the user had open. Those callers use this to tell
 * "the build moved under us, offer a reload" apart from a genuine failure, so the two
 * definitions cannot drift.
 */
export function isDynamicImportFailure(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message ?? error ?? '');
  return /dynamically imported module|importing a module script failed|ChunkLoadError/i.test(msg);
}

/**
 * Recovers from lazy chunks that vanish under a running tab.
 *
 * Every feature route is lazy, so navigating fetches a content-hashed chunk named
 * by the build the tab loaded. A redeploy replaces those files, so a tab holding
 * the previous index.html asks for a chunk the server no longer has. The server's
 * SPA fallback answers with index.html, and the browser — expecting a module —
 * fails the dynamic import. The user is then stuck: the route never loads, and no
 * amount of clicking helps because the stale module graph is already in memory.
 *
 * Reloading picks up the current index.html and resolves it. The reload is
 * attempted once within a short window, so a genuinely broken chunk (a real 404, a
 * network fault) surfaces as an error instead of trapping the tab in a reload loop.
 *
 * The durable fix is server-side: serve index.html with `Cache-Control: no-cache`
 * so a redeploy is picked up immediately, and serve the content-hashed assets
 * immutable. This handler is the safety net for tabs already open when that happens.
 */
@Injectable()
export class StaleChunkErrorHandler implements ErrorHandler {
  /** Key holding the timestamp of the last recovery reload. */
  private static readonly KEY = 'beacon_chunk_reload_at';

  /** A second failure inside this window is treated as real, not staleness. */
  private static readonly WINDOW_MS = 30_000;

  constructor() {
    // A failed lazy-route import does not always arrive through Angular's error
    // channel: depending on build and router version it can surface only as an
    // unhandled rejection on the window. Both paths are watched so recovery does
    // not depend on which one reports it.
    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', ev => {
        if (this.isStaleChunkError((ev as PromiseRejectionEvent).reason)) this.tryReload();
      });
      window.addEventListener('error', ev => {
        if (this.isStaleChunkError((ev as ErrorEvent).error ?? (ev as ErrorEvent).message)) this.tryReload();
      });
    }
  }

  handleError(error: unknown): void {
    if (this.isStaleChunkError(error) && this.tryReload()) return;
    // Preserve default behaviour for everything else.
    console.error(error);
  }

  /** See isDynamicImportFailure above — shared so on-demand library loaders that
   *  handle their own rejection classify it exactly the way this handler does. */
  private isStaleChunkError(error: unknown): boolean {
    return isDynamicImportFailure(error);
  }

  /** Reloads once per window; returns false when the budget is spent. */
  private tryReload(): boolean {
    let last = 0;
    try { last = Number(sessionStorage.getItem(StaleChunkErrorHandler.KEY)) || 0; } catch { /* storage blocked */ }
    const now = Date.now();
    if (now - last < StaleChunkErrorHandler.WINDOW_MS) return false;
    try { sessionStorage.setItem(StaleChunkErrorHandler.KEY, String(now)); } catch { /* storage blocked */ }
    console.warn('[beacon] a lazily-loaded chunk was missing — this build is stale, reloading once to pick up the current one');
    window.location.reload();
    return true;
  }
}
